import { usePostHog } from "@posthog/react";
import {
	createFileRoute,
	Outlet,
	redirect,
	useLocation,
	useNavigate,
} from "@tanstack/react-router";
import { LogOut, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Toaster } from "sonner";
import { AppSidebar } from "@/components/app-sidebar";
import { XanderButton } from "@/components/xander/xander-button";
import { XanderPanel } from "@/components/xander/xander-panel";
import { Button } from "@/components/ui/button";
import {
	Sheet,
	SheetContent,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";
import { TooltipProvider } from "@/components/ui/tooltip";
import { usePostHogIdentify } from "@/hooks/use-posthog-identify";
import { useSession } from "@/hooks/use-session";
import { useXander } from "@/hooks/use-xander";
import { useXanderLightbulb } from "@/hooks/use-xander-lightbulb";
import { apiService } from "@/lib/api-service";
import { authService } from "@/lib/auth-service";
import { router } from "@/main";
import { PageContextProvider } from "@/providers/page-context-provider";
import { SessionProvider } from "@/providers/session-provider";
import type { SessionData } from "@/types/session";


export const Route = createFileRoute("/_app")({
	component: RouteComponent,
	beforeLoad: async ({ context }) => {
		// Accept impersonation token from admin portal via URL fragment.
		// Fragments (#) are never sent to the server, so the token won't
		// leak via request logs, referrer headers, or CDN/ALB logs.
		const hash = window.location.hash;
		if (hash.startsWith("#_imp_token=")) {
			const impToken = decodeURIComponent(hash.slice("#_imp_token=".length));
			authService.setImpersonationToken(impToken, 3600);
			window.history.replaceState({}, "", window.location.pathname + window.location.search);
		}

		if (!context.auth.isAuthenticated()) {
			console.log("User not authenticated, redirecting to /login");
			throw redirect({ to: "/login" });
		}
	},
	loader: async () => {
		// Fetch session data including campaign info
		console.log("[_app] Fetching session data from /auth/me...");
		const session = await apiService.getSession();

		// Skip routing gates for admins
		const isAdmin = session?.user?.is_admin;

		// TOS gate: applies to all non-admin users, even without a campaign.
		// Uses server-authoritative needs_acceptance to catch both null AND stale versions.
		if (!isAdmin && session?.user) {
			const currentPath = window.location.pathname;
			if (!currentPath.startsWith("/onboarding/accept-tos")) {
				const tosStatus = await apiService.getTosStatus();
				if (tosStatus.needs_acceptance) {
					throw redirect({ to: "/onboarding/accept-tos" });
				}
			}
		}

		if (!isAdmin && session?.campaign) {
			const currentPath = window.location.pathname;
			const step = session.campaign.onboarding_step;

			if (step === "accept_tos") {
				if (!currentPath.startsWith("/onboarding/accept-tos")) {
					throw redirect({ to: "/onboarding/accept-tos" });
				}
			} else if (step === "interview_intro") {
				// Allow onboarding and interview paths
				if (
					!currentPath.startsWith("/onboarding") &&
					!currentPath.startsWith("/interview")
				) {
					throw redirect({ to: "/onboarding/interview-intro" });
				}
			} else if (step === "generating") {
				if (!currentPath.startsWith("/onboarding/generating")) {
					throw redirect({ to: "/onboarding/generating" });
				}
			} else if (step === "complete") {
				// Force users to strategy reveal on first login after strategy is ready
				if (
					session.campaign.strategy_status === "ready" &&
					!currentPath.startsWith("/strategy") &&
					!localStorage.getItem(
						`strategy_reveal_seen_${session.campaign.id}`,
					)
				) {
					throw redirect({ to: "/strategy" });
				}
			}
		}

		return { session };
	},
});

/**
 * Volunteers (campaign membership role "volunteer") work exclusively in the
 * Common mobile (iOS) app — the web app is not a supported surface for them.
 * Rather than show an empty/half-broken dashboard, block the web app entirely
 * with a clear hand-off. (Real PRD-40 volunteers are phone-only and can't
 * password-login here anyway; this guards the case where a web-capable user
 * holds a volunteer role on the active campaign.)
 */
function VolunteerMobileNotice() {
	const handleLogout = async () => {
		try {
			await apiService.logout();
		} catch {
			// fall through to local logout
		}
		authService.clearTokens();
		router.navigate({ to: "/login" });
	};

	return (
		<div className="flex h-dvh w-full items-center justify-center bg-background p-6">
			<div className="max-w-md text-center space-y-4">
				<h1 className="text-xl font-semibold tracking-tight">
					Volunteer tools live in the mobile app
				</h1>
				<p className="text-muted-foreground text-sm">
					Your volunteer shifts, calling, and texting are all in the{" "}
					<a
						href="https://common.americamajority.org"
						target="_blank"
						rel="noopener noreferrer"
						className="font-medium text-primary underline underline-offset-4 hover:no-underline"
					>
						Common mobile app
					</a>
					. Open it on your phone to get started — there's nothing to do here
					on the web.
				</p>
				<Button variant="outline" onClick={handleLogout}>
					Log out
				</Button>
			</div>
		</div>
	);
}

function RouteComponent() {
	const { session } = Route.useLoaderData();

	// Volunteers are routed to the mobile app — block the web surface.
	if (
		session?.membership?.role === "volunteer" &&
		!session?.user?.is_superuser
	) {
		return <VolunteerMobileNotice />;
	}

	return (
		<SessionProvider session={session as SessionData}>
			<PageContextProvider>
				<AppShell />
			</PageContextProvider>
		</SessionProvider>
	);
}

function AppShell() {
	const navigate = useNavigate();
	// PostHog: identify user and track page views
	usePostHogIdentify();
	const posthog = usePostHog();
	const session = useSession();
	const xander = useXander();
	const lightbulb = useXanderLightbulb(session.campaign?.id ?? 0);
	useEffect(() => {
		const unsub = router.subscribe("onResolved", () => {
			posthog?.capture("$pageview", {
				$current_url: window.location.href,
			});
		});
		return unsub;
	}, [posthog]);

	const [isImpersonating] = useState(() => authService.isImpersonating());

	const handleLogout = async () => {
		if (isImpersonating) {
			authService.clearImpersonationToken();
			window.close();
			return;
		}
		try {
			await apiService.logout();
		} catch (error) {
			// Continue with local logout even if server call fails
			console.error("Server logout failed:", error);
		}
		authService.clearTokens();
		posthog?.reset();
		navigate({ to: "/login" });
	};

	const location = useLocation();
	const isFullscreenRoute =
		location.pathname.includes("/interview/session") ||
		location.pathname.startsWith("/onboarding");

	// Mobile/tablet nav drawer state. The sidebar is hidden by default on
	// viewports < lg (1024px) and surfaced via a Sheet from the top bar; it
	// renders inline only at lg+. Collapsing it below lg keeps tablet-width
	// content (e.g. the outreach table) from being squeezed by the 256px rail.
	const [mobileNavOpen, setMobileNavOpen] = useState(false);
	useEffect(() => {
		// Close the mobile drawer on any route change.
		setMobileNavOpen(false);
	}, [location.pathname]);

	return (
		<TooltipProvider>
			<div className="flex h-dvh overflow-hidden w-full">
				{!isFullscreenRoute && (
					<div className="hidden lg:flex">
						<AppSidebar />
					</div>
				)}
				<main className="flex-1 flex flex-col min-w-0">
					{isImpersonating && (
						<div className="sticky top-0 z-20 flex items-center justify-between px-4 py-2 bg-amber-100 border-b border-amber-300 text-amber-900 text-sm">
							<span className="font-medium">
								You are viewing as this user (read-only
								impersonation)
							</span>
							<Button
								variant="ghost"
								size="sm"
								className="h-6 px-2 text-amber-900 hover:bg-amber-200"
								onClick={() => {
									authService.clearImpersonationToken();
									window.close();
								}}
							>
								<X className="h-3 w-3 mr-1" />
								Close
							</Button>
						</div>
					)}
					{!isFullscreenRoute && (
						<div className="sticky top-0 z-10 flex items-center justify-between p-2 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
							{/* Hamburger that opens the sidebar in a Sheet below lg.
							    Hidden at lg+ where the sidebar is always present. */}
							<Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
								<SheetTrigger asChild>
									<Button
										variant="ghost"
										size="icon"
										className="lg:hidden h-11 w-11"
										aria-label="Open navigation"
									>
										<Menu className="h-5 w-5" />
									</Button>
								</SheetTrigger>
								<SheetContent side="left" className="p-0 w-[280px] sm:w-[280px]">
									<SheetTitle className="sr-only">Navigation</SheetTitle>
									<AppSidebar />
								</SheetContent>
							</Sheet>
							{/* Spacer at lg+ so the right-side controls stay flush-right. */}
							<div className="hidden lg:block" />
							<div className="flex items-center gap-2">
								<XanderButton
									onClick={xander.toggle}
									isOpen={xander.isOpen}
									shouldAnimate={lightbulb.shouldAnimate}
								/>
								<Button variant="ghost" size="sm" onClick={handleLogout}>
									<LogOut className="h-4 w-4 mr-2" />
									Log out
								</Button>
							</div>
						</div>
					)}
					<div className="flex-1 overflow-auto">
						<Outlet />
					</div>
				</main>
				<XanderPanel isOpen={xander.isOpen} onClose={xander.close} xander={xander} />
			</div>
			<Toaster position="top-right" richColors closeButton />
		</TooltipProvider>
	);
}
